import React, { useState } from 'react';
import './UserDashboard.css';

const DocumentationPanel = () => {
  const [selectedSection, setSelectedSection] = useState('getting-started');
  const [showFAQ, setShowFAQ] = useState(false);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [showGameChanger, setShowGameChanger] = useState(false);

  const documentationSections = {
    'getting-started': {
      title: '🚀 Getting Started',
      content: `
# Welcome to VlogClip AI

Turn long YouTube videos into viral short clips for TikTok, Instagram Reels, and YouTube Shorts — with AI-powered captions, face tracking, and emoji overlays.

## How It Works

1. **Paste a YouTube URL** — any public video from 1 to 90 minutes
2. **AI analyses the transcript** — Whisper transcribes the audio, GPT selects the most engaging moments
3. **Download your clips** — portrait-optimised clips with captions, ready to post

## What You Get Per Video

— **5 clips per video** (Pro) · **8 clips per video** (Business) — AI-selected highlights scored by engagement potential
— **Face-aware cropping** — DNN face detection centres the speaker in portrait frame
— **Auto-captions** — word-level synced captions in 11 styles
— **AI viral hooks** — auto-generated text overlay on the first 3 seconds (Pro/Business)
— **Emoji overlays** — keyword-triggered emoji images in 31 languages (Pro/Business)
— **Engagement badges** — viral potential score on each clip (Pro/Business)
— **Thumbnails** — auto-generated portrait thumbnails for each clip

## Aspect Ratios

— **9:16** — TikTok, Instagram Reels, YouTube Shorts
— **4:5** — Instagram Feed, Facebook
— **1:1** — Instagram, LinkedIn, X (Twitter)

## Caption Styles

11 styles available (all plans get Minimal, Pro/Business unlock all):
Minimal · Word Highlight · Hormozi Bold · CapCut Pop-In · Kinetic Zoom · Fight Hype · Karaoke · Bold Pop · Explosion · Gradient Pop · Flash

Each style supports 6 colour options: Yellow, Green, Cyan, Pink, Red, Orange.

## Plans

| Feature | Free | Creator Pro | Business |
|---|---|---|---|
| **Price** | £0 | £9.99/mo | £24.99/mo |
| **Videos/month** | 5 | 20 | 60 |
| **Clips per video** | 3 | 5 | 8 |
| **Max video length** | 30 min | 60 min | 90 min |
| **Max clip length** | 15s | 30s | 60s |
| **Resolution** | 720p | 1080p HD | 1080p HD |
| **Watermark** | Yes | No | No |
| **Batch processing** | — | 3 URLs | 4 URLs |
| **File upload** | — | Up to 2 GB | Up to 5 GB |
| **Caption translation** | — | 31 languages | 31 languages |
| **AI hooks & emoji** | — | ✓ | ✓ |
| **Custom branding** | — | — | ✓ |
| **Clip storage** | — | 5 hours | 10 hours |

## Output Specs

— **Format**: MP4 (H.264, AAC audio)
— **Bitrate**: 4500 kbps (Pro/Business) · 2000 kbps (Free)
— **Preset**: fast (optimised for quality + speed)

## Device & Browser Support

— **Desktop**: Chrome, Firefox, Safari, Edge (latest versions)
— **Mobile**: Fully responsive — iPhone, iPad, Android
— **Downloads**: Direct MP4 download on all platforms
      `
    },
    'features': {
      title: '✨ Features Guide',
      content: `
# Features Guide

Everything VlogClip AI can do.

## Caption Styles

Choose from 11 styles in the Generator or Batch Processor:

— **Minimal** — clean, lowercase white text (Free)
— **Word Highlight** — active word highlighted in colour
— **Hormozi Bold** — thick black + coloured outline, uppercase
— **CapCut Pop-In** — bold pop with cyan accent
— **Kinetic Zoom** — pink-accented motion style
— **Fight Hype** — 4-layer drawtext: black → red → colour → white fill
— **Karaoke** — word-by-word coloured box highlight with dark shadow borders for readability (like karaoke TV)
— **Bold Pop** — pink impact text with heavy shadow
— **Explosion** — yellow accent with scaling emphasis
— **Gradient Pop** — gradient colour on active words
— **Flash** — one word at a time, bold yellow text with thick black outline (TikTok style)

All styles support 6 colours: Yellow, Green, Cyan, Pink, Red, Orange.

## Caption Translation

Translate captions into 31 languages using AI. The translation appears as the caption text — the original audio remains unchanged.

Supported: English, Spanish, French, German, Portuguese, Italian, Dutch, Arabic, Hindi, Japanese, Korean, Chinese, Russian, Turkish, Polish, Swedish, Norwegian, Danish, Finnish, Greek, Czech, Romanian, Hungarian, Thai, Vietnamese, Indonesian, Malay, Ukrainian, Hebrew, Filipino, Bengali, Tamil, Lithuanian.

## AI Viral Hooks

Auto-generated text overlay on the first 3 seconds of each clip. The AI reads the clip transcript and creates a short, attention-grabbing hook like "You won't believe this!" or "This changes everything."

— Generates in the video's language automatically
— Supports CJK fonts (Japanese, Chinese, Korean)
— Gold/yellow gradient text with fade-in animation

## Emoji Overlays

Keyword-triggered emoji images appear alongside captions. Set to "Auto" for automatic detection or pick a specific emoji.

— 2,160+ curated trigger keywords across 31 languages (generic words removed for accurate placement)
— 271 emoji categories including 72 country flags, animals, food, sports, music, and 22 Gen Z/TikTok slang categories (slay, bussin, sigma, tea, vibe, drip, etc.)
— Works with translated captions too

## Face-Aware Cropping

DNN face detection samples 5 frames per clip and calculates a weighted average position. Three modes:
— **Single face** — locks onto the nearest face to camera (80% area + 20% confidence)
— **Softened crop** — 70% face position + 30% centre blend so nearby people don't get cut off
— **Two faces close together** — centres on the midpoint between both faces

## Podcast Mode (Automatic)

When a 2-person interview or podcast layout is detected, clips automatically use full video width with dark bars top and bottom instead of cropping to the empty middle. All captions, hooks, engagement badges, watermarks, and business logos scale down ~35% to fit the podcast frame. No settings needed — it's fully automatic.

## File Upload (Pro/Business)

Upload your own video files instead of pasting a YouTube URL:
— **Pro**: up to 2 GB
— **Business**: up to 5 GB
— Same AI processing, captions, and features as YouTube URLs

## Batch Processing (Pro/Business)

Process multiple YouTube URLs in parallel:
— **Pro**: 3 URLs per batch
— **Business**: 4 URLs per batch

All videos download and encode simultaneously. Same quality and features as single processing.

## Custom Branding (Business)

Add your logo or text watermark to every generated clip:
— Upload a logo image (PNG recommended)
— Or set a text watermark
— Choose position (top-left, top-right, bottom-left, bottom-right)
— Adjust opacity

## AI Help Assistant

Click the chat bubble (desktop) or the AI Help tab (mobile) to ask questions about VlogClip AI. The assistant knows your plan, features, and can answer in 33 languages.

— 8 pre-written FAQ topics (instant, free)
— Custom questions answered by AI
— Daily limits: 5 (Free) / 20 (Pro) / 100 (Business)

## Language Selector

The entire UI can be viewed in 33 languages using the language selector in the navigation bar. The AI help assistant also responds in your selected language.

## Cross-Tab Logout

Logging out on one browser tab automatically logs out all other open tabs. This uses the browser's localStorage storage event for instant sync across tabs.
      `
    },
    'best-practices': {
      title: '💡 Best Practices',
      content: `
# Best Practices for Viral Clips

## Choosing the Right Videos

### What Works Best
— **Tutorials & How-Tos** — step-by-step guides with clear takeaways
— **Reaction & Commentary** — expressive moments with strong opinions
— **Interviews & Podcasts** — quotable soundbites and hot takes
— **Behind-the-Scenes** — authentic, unscripted moments
— **Product Reviews** — key verdict moments viewers want to share

### What to Avoid
— Videos shorter than 60 seconds (not enough content for the AI)
— Music-only videos with no speech
— Private, age-restricted, or playlist URLs
— Livestream replays with long idle sections

## Platform Tips

### TikTok (9:16)
— 15-30 second clips get the best engagement
— The first 3 seconds decide if viewers stay — AI hooks help here
— Use Karaoke or Hormozi Bold caption styles for maximum impact

### Instagram Reels (9:16) & Feed (4:5)
— Use 9:16 for Reels, 4:5 for feed posts
— HD source videos produce the best output
— Pair with relevant hashtags

### YouTube Shorts (9:16)
— Must be under 60 seconds
— Use clips as teasers driving traffic to the full video
— Add a CTA in your Shorts description

### LinkedIn (1:1)
— Professional, insightful moments work best
— Conference talks, expert commentary, interviews
— Square format fits the LinkedIn feed naturally

## Batch Processing Tips

— Mix content types for variety across channels
— All videos in a batch use the same caption style and settings
— Processing time: 3-8 minutes for most batches
— You can close the tab — clips are emailed when ready
      `
    },
    'troubleshooting': {
      title: '🔧 Troubleshooting',
      content: `
# Troubleshooting

## Processing Issues

### "Video processing failed"
1. Video may be private, unlisted, or age-restricted — use a public URL
2. Remove playlist or tracking parameters — paste only the direct video link (youtube.com/watch?v=...)
3. Try again in a few minutes — YouTube occasionally rate-limits downloads
4. Videos over 90 minutes may time out

### "Email verification required"
— Check your inbox for the verification email
— Click the "Resend Email" button on the dashboard banner
— Check spam/junk folders
— Google OAuth users are auto-verified

### Processing seems stuck
**Normal processing times:**
— Single video: 2-5 minutes
— Batch (3-4 videos): 4-8 minutes
— Long videos (60-90 min): up to 12 minutes

You can close the browser tab — processing continues on the server and you'll receive an email with your clips when they're ready.

### Captions out of sync
— This can happen with videos that have unusual encoding. Try a different video or caption style.
— Batch processing captions are generally more accurate than single-generate.

## Download Issues

### Clips not downloading
1. Check your browser allows downloads from vlogclipai.com
2. Try right-clicking and selecting "Save As"
3. On mobile, check your Downloads folder
4. Try a different browser if issues persist

### Clips expired
— **Pro plan**: clips stored for 5 hours
— **Business plan**: clips stored for 10 hours
— **Free plan**: no storage — download immediately
— Generate again if your clips have expired

## Payments & Billing

All payments are processed through **Stripe**. Your card details are never stored on our servers.

### Upgrading
— Click "Upgrade" on your dashboard
— Complete payment on the secure Stripe checkout page
— Visa, Mastercard, and Amex accepted
— Subscription activates instantly

### Manage Subscription (Stripe Customer Portal)
— Go to your Billing page and click "Manage Subscription"
— Opens the Stripe Customer Portal where you can update your payment method, change card details, or cancel your plan
— All changes take effect immediately or at end of billing period

### Billing History
— Your Billing page shows real invoices from Stripe
— Each invoice shows date, amount, payment status, and a link to view the full receipt
— Invoice history updates automatically after each payment

### Cancelling
— Click "Manage Subscription" on your Billing page to cancel via Stripe Customer Portal
— Or email support@vlogclipai.com to cancel
— Cancellation takes effect at the end of your billing period
— You keep access to all paid features until the period ends
— No contracts — cancel any time

### Usage Limits
— Counters reset every 30 days from your account creation date
— **Free**: 5 videos/month
— **Pro**: 20 videos/month
— **Business**: 60 videos/month

### Refunds
— Contact support@vlogclipai.com within 7 days of purchase
— Reviewed case-by-case

## Mobile Tips

### iPhone / iPad
— Safari or Chrome recommended
— Downloads save to Files app
— Enable "Allow Downloads" in Safari settings if prompted

### Android
— Chrome recommended
— Downloads save to your Downloads folder
— Grant storage permissions if prompted

## Need More Help?

— **AI Assistant**: Click the chat bubble on any page
— **Email**: support@vlogclipai.com
— **Response time**: Within 24 hours
      `
    }
  };

  return (
    <div className="user-dashboard">
        <div style={{padding:'0 0 8px 0'}}><button className="dash-back-btn" onClick={() => { window.history.pushState({section:'dashboard'}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate', {state:{section:'dashboard'}})); }}>🏠 ← Dashboard</button></div>
      <div className="dashboard-header">
        <div className="user-info">
          <h1>📚 Documentation</h1>
          <p className="user-email">Learn everything about VlogClip AI</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px', flexDirection: window.innerWidth <= 768 ? 'column' : 'row', position: 'relative', zIndex: 1 }}>
        {/* Sidebar Navigation */}
        <div style={{
          minWidth: window.innerWidth <= 768 ? '100%' : '250px',
          maxWidth: window.innerWidth <= 768 ? '100%' : '280px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          height: 'fit-content'
        }}>
          <h3 style={{ 
            color: '#ffffff', 
            marginBottom: '16px', 
            fontSize: '18px',
            fontWeight: '700'
          }}>
            📖 Table of Contents
          </h3>
          <div id="doc-toc"></div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {Object.entries(documentationSections).map(([key, section]) => (
              <button
                key={key}
                onClick={() => { setSelectedSection(key); setTimeout(() => { const el = document.getElementById('doc-content-section'); if (el && window.innerWidth <= 1024) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); }}
                style={{
                  padding: '12px 16px',
                  background: selectedSection === key ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
                  border: selectedSection === key ? '1px solid rgba(99, 102, 241, 0.4)' : '1px solid transparent',
                  borderRadius: '8px',
                  color: selectedSection === key ? '#ffffff' : '#94a3b8',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                  fontSize: '14.5px',
            fontWeight: '600',
                  fontWeight: selectedSection === key ? '600' : '500'
                }}
                onMouseOver={(e) => {
                  if (selectedSection !== key) {
                    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.target.style.color = '#cbd5e1';
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedSection !== key) {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#94a3b8';
                  }
                }}
              >
                {section.title}
              </button>
            ))}
          </div>

          <div style={{
            marginTop: '24px',
            padding: '16px',
            background: 'linear-gradient(135deg, rgba(139,92,246,0.1), rgba(6,182,212,0.08))',
            border: '1px solid rgba(139,92,246,0.25)',
            borderRadius: '12px'
          }}>
            <h4 style={{ color: '#a78bfa', marginBottom: '8px', fontSize: '14px',
            fontWeight: '700' }}>
              🤖 Need Help?
            </h4>
            <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 12px 0', lineHeight: '1.5' }}>
              Ask our AI assistant — it knows everything about VlogClip AI and responds in your language.
            </p>
            <button
              onClick={() => { window.dispatchEvent(new CustomEvent('open-help-chat')); }}
              style={{
                width: '100%',
                padding: '10px',
                background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '700'
              }}
            >
              Ask AI Assistant
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div style={{
          flex: 1,
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '16px',
          padding: window.innerWidth <= 768 ? '16px' : '32px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          maxHeight: window.innerWidth <= 768 ? 'none' : '80vh',
          overflowY: window.innerWidth <= 768 ? 'visible' : 'auto'
        }}>
          <div id="doc-content-section" style={{color:'#cbd5e1',lineHeight:'1.7',fontSize:'14px'}} dangerouslySetInnerHTML={{__html: (() => {
            let md = documentationSections[selectedSection].content;
            // Convert markdown to HTML
            md = md.replace(/^# (.+)$/gm, '<h1 style="color:#f1f5f9;font-size:24px;font-weight:800;margin:0 0 16px;letter-spacing:-0.3px">$1</h1>');
            md = md.replace(/^### (.+)$/gm, '<h3 style="color:#c4b5fd;font-size:14px;font-weight:700;margin:20px 0 8px">$1</h3>');
            md = md.replace(/^## (.+)$/gm, '<h2 style="color:#e2e8f0;font-size:17px;font-weight:700;margin:28px 0 12px;padding-bottom:8px;border-bottom:1px solid rgba(255,255,255,0.06)">$1</h2>');
            md = md.replace(/\*\*(.+?)\*\*/g, '<strong style="color:#f1f5f9;font-weight:700">$1</strong>');
            md = md.replace(/^— (.+)$/gm, '<div style="display:flex;gap:8px;padding:5px 0;align-items:flex-start"><span style="color:#8b5cf6;font-size:16px;line-height:1.2;flex-shrink:0">•</span><span>$1</span></div>');
            md = md.replace(/^\d+\. (.+)$/gm, (match, p1, offset, str) => {
              const before = str.substring(0, offset);
              const num = (before.match(/^\d+\. /gm) || []).length + 1;
              return '<div style="display:flex;gap:10px;padding:5px 0;align-items:flex-start"><span style="color:#8b5cf6;font-weight:800;font-size:13px;min-width:20px;flex-shrink:0">' + num + '.</span><span>' + p1 + '</span></div>';
            });
            // Tables
            md = md.replace(/\|(.+)\|\n\|[-| ]+\|\n((?:\|.+\|\n?)+)/g, (match, header, body) => {
              const headers = header.split('|').map(h => h.trim()).filter(Boolean);
              const rows = body.trim().split('\n').map(r => r.split('|').map(c => c.trim()).filter(Boolean));
              let t = '<div style="overflow-x:auto;margin:12px 0"><table style="width:100%;border-collapse:collapse;font-size:12px"><thead><tr>';
              headers.forEach(h => t += '<th style="padding:8px 10px;text-align:left;border-bottom:2px solid rgba(139,92,246,0.3);color:#a78bfa;font-weight:700;white-space:nowrap">' + h + '</th>');
              t += '</tr></thead><tbody>';
              rows.forEach(r => { t += '<tr>'; r.forEach(c => t += '<td style="padding:8px 10px;border-bottom:1px solid rgba(255,255,255,0.04);color:#cbd5e1">' + c + '</td>'); t += '</tr>'; });
              t += '</tbody></table></div>';
              return t;
            });
            md = md.replace(/\n\n+/g, '<div style="height:8px"></div>');
            md = md.replace(/\n/g, '');
            return md;
          })()}} />
          <button onClick={() => { const el = document.getElementById('doc-toc'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} style={{ marginTop: '20px', width: '100%', padding: '12px', background: 'rgba(99, 102, 241, 0.15)', border: '1px solid rgba(99, 102, 241, 0.3)', borderRadius: '10px', color: '#a5b4fc', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>Return to Table of Contents</button>
        </div>
      </div>

      {/* Quick Links */}
      <div id="doc-quick-links" style={{
        marginTop: '24px',
        display: 'grid',
        gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: '16px',
        position: 'relative',
        zIndex: 1
      }}>
        <button
          onClick={() => { const opening = !showFAQ; setShowFAQ(opening); setShowReleaseNotes(false); setShowGameChanger(false); if (opening) setTimeout(() => { const el = document.getElementById('section-faq'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); }}
          style={{
            padding: '16px',
            background: showFAQ ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255, 255, 255, 0.05)',
            border: showFAQ ? '1px solid rgba(139, 92, 246, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            color: '#ffffff',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(99, 102, 241, 0.2)'; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <div style={{ fontSize: '20px', marginBottom: '8px' }}>❓</div>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>FAQs</div>
          <div style={{ fontSize: '12px', color: '#94a3b8' }}>Common questions answered</div>
        </button>

        <button
          onClick={() => { window.history.pushState({section:'support'}, '', '/support'); window.dispatchEvent(new PopStateEvent('popstate', {state:{section:'support'}})); }}
          style={{
            padding: '16px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            color: '#ffffff',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(99, 102, 241, 0.2)'; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <div style={{ fontSize: '20px', marginBottom: '8px' }}>📩</div>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>Contact Support</div>
          <div style={{ fontSize: '12px', color: '#94a3b8' }}>Get help from our team</div>
        </button>

        <button
          onClick={() => { const opening = !showReleaseNotes; setShowReleaseNotes(opening); setShowFAQ(false); setShowGameChanger(false); if (opening) setTimeout(() => { const el = document.getElementById('section-release'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); }}
          style={{
            padding: '16px',
            background: showReleaseNotes ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255, 255, 255, 0.05)',
            border: showReleaseNotes ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            color: '#ffffff',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(99, 102, 241, 0.2)'; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <div style={{ fontSize: '20px', marginBottom: '8px' }}>🆕</div>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>Release Notes</div>
          <div style={{ fontSize: '12px', color: '#94a3b8' }}>Latest updates and features</div>
        </button>

        <button
          onClick={() => { const opening = !showGameChanger; setShowGameChanger(opening); setShowFAQ(false); setShowReleaseNotes(false); if (opening) setTimeout(() => { const el = document.getElementById('section-gamechanger'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); }}
          style={{
            padding: '16px',
            background: showGameChanger ? 'rgba(245, 158, 11, 0.15)' : 'rgba(255, 255, 255, 0.05)',
            border: showGameChanger ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            color: '#ffffff',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(99, 102, 241, 0.2)'; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <div style={{ fontSize: '20px', marginBottom: '8px' }}>🔥</div>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>Why VlogClip AI?</div>
          <div style={{ fontSize: '12px', color: '#94a3b8' }}>A game changer for creators</div>
        </button>
      </div>

      {/* FAQ Section */}
      {showFAQ && (
        <div id="section-faq" style={{ marginTop: '24px', padding: '24px', background: 'rgba(139, 92, 246, 0.05)', border: '1px solid rgba(139, 92, 246, 0.2)', borderRadius: '16px' }}>
          <h3 style={{ color: '#8b5cf6', marginBottom: '20px', fontSize: '20px' }}>Frequently Asked Questions</h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ color: '#fff', fontWeight: '600', marginBottom: '8px' }}>How does VlogClip AI work?</div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>Simply paste a YouTube URL, and our AI analyses the video to identify the most engaging moments. It then creates short-form clips optimised for TikTok, Instagram Reels, and YouTube Shorts with captions and platform-specific formatting.</div>
            </div>
            
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ color: '#fff', fontWeight: '600', marginBottom: '8px' }}>What video formats are supported?</div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>We support any public YouTube video. The AI works best with videos between 5-60 minutes containing clear speech. Educational, entertainment, and tutorial content produce the best clips.</div>
            </div>
            
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ color: '#fff', fontWeight: '600', marginBottom: '8px' }}>Can I use the clips commercially?</div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>Commercial usage rights are included with the Business plan. Free and Creator Pro plans are for personal and non-commercial use. Always ensure you have rights to the original video content.</div>
            </div>
            
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ color: '#fff', fontWeight: '600', marginBottom: '8px' }}>How long does processing take?</div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>Most videos are processed in 2-5 minutes. Batch processing of multiple videos runs in parallel for faster results. Processing time depends on video length and server load.</div>
            </div>
            
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ color: '#fff', fontWeight: '600', marginBottom: '8px' }}>Can I cancel my subscription anytime?</div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>Yes, you can cancel your subscription at any time. Go to your Billing page and click "Manage Subscription" to open the Stripe Customer Portal, where you can cancel directly. Your paid features remain active until the end of your billing period. After that, your account reverts to the Free Tier automatically.</div>
            </div>
          </div>
          <button onClick={() => { setShowFAQ(false); setTimeout(() => { const el = document.getElementById('doc-quick-links'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); }} style={{ marginTop: '20px', width: '100%', padding: '12px', background: 'rgba(139, 92, 246, 0.15)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '10px', color: '#a78bfa', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>✕ Close FAQs</button>
        </div>
      )}

      {/* Release Notes Section */}
      {showReleaseNotes && (
        <div id="section-release" style={{ marginTop: '24px', padding: '24px', background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '16px' }}>
          <h3 style={{ color: '#10b981', marginBottom: '20px', fontSize: '20px' }}>Release Notes</h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ color: '#fff', fontWeight: '600' }}>v3.0 - Billing & Polish</span>
                <span style={{ color: '#10b981', fontSize: '12px', fontWeight: '600', background: 'rgba(16,185,129,0.1)', padding: '4px 8px', borderRadius: '6px' }}>Apr 2026</span>
              </div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>Stripe Customer Portal for self-service subscription management (update payment, cancel). Real billing history with Stripe invoices. Karaoke caption dark shadow borders for better readability. VlogClip logo and deep purple theme added to all email templates. Emoji keyword cleanup for more accurate placement. Google OAuth friendly error handling. Demo video and mobile layout fixes.</div>
            </div>

            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ color: '#fff', fontWeight: '600' }}>v2.5 - Speed Upgrade</span>
                <span style={{ color: '#10b981', fontSize: '12px', fontWeight: '600', background: 'rgba(16,185,129,0.1)', padding: '4px 8px', borderRadius: '6px' }}>Feb 2026</span>
              </div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>10x faster video processing with section-based downloads. Batch processing now runs in parallel. New admin dashboard with user management and revenue tracking.</div>
            </div>
            
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ color: '#fff', fontWeight: '600' }}>v2.0 - Platform Launch</span>
                <span style={{ color: '#10b981', fontSize: '12px', fontWeight: '600', background: 'rgba(16,185,129,0.1)', padding: '4px 8px', borderRadius: '6px' }}>Jan 2026</span>
              </div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>Full HTTPS deployment on vlogclipai.com. Three-tier pricing (Free, Creator Pro, Business). AI-powered hotspot detection. Multi-platform clip optimisation for TikTok, Instagram, YouTube Shorts, Twitter, and LinkedIn.</div>
            </div>
            
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ color: '#fff', fontWeight: '600' }}>v1.0 - Beta Release</span>
                <span style={{ color: '#10b981', fontSize: '12px', fontWeight: '600', background: 'rgba(16,185,129,0.1)', padding: '4px 8px', borderRadius: '6px' }}>Dec 2025</span>
              </div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>Initial release with YouTube video processing, automatic clip generation, watermark support, and basic dashboard. Single video processing with FFmpeg encoding.</div>
            </div>
          </div>
          <button onClick={() => { setShowReleaseNotes(false); setTimeout(() => { const el = document.getElementById('doc-quick-links'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); }} style={{ marginTop: '20px', width: '100%', padding: '12px', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '10px', color: '#34d399', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>✕ Close Release Notes</button>
        </div>
      )}

      {/* Why VlogClip AI Section */}
      {showGameChanger && (
        <div id="section-gamechanger" style={{ marginTop: '24px', padding: '24px', background: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: '16px' }}>
          <h3 style={{ color: '#f59e0b', marginBottom: '20px', fontSize: '20px' }}>Why VlogClip AI is a Game Changer for Content Creators</h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ color: '#fff', fontWeight: '600', marginBottom: '8px', fontSize: '16px' }}>⏱️ Save Hours Every Week</div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>Manually editing clips from long-form videos takes 2-4 hours per video. VlogClip AI does it in minutes. That is time you can spend creating more content, engaging with your audience, or growing your brand.</div>
            </div>
            
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ color: '#fff', fontWeight: '600', marginBottom: '8px', fontSize: '16px' }}>📈 Multiply Your Content Reach</div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>One YouTube video becomes 3 viral clips for TikTok, Instagram Reels, and YouTube Shorts. That is 4x the content from a single recording session. Creators using short-form repurposing see 300% more engagement on average.</div>
            </div>
            
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ color: '#fff', fontWeight: '600', marginBottom: '8px', fontSize: '16px' }}>🤖 AI Finds Your Best Moments</div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>Our AI analyses speech patterns, energy levels, and content structure to identify the moments most likely to go viral. No more guessing which 15 seconds will perform best - the AI already knows.</div>
            </div>
            
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ color: '#fff', fontWeight: '600', marginBottom: '8px', fontSize: '16px' }}>🎯 Platform-Perfect Every Time</div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>Each clip is automatically formatted with the correct aspect ratio, duration limits, and captions for its target platform. No need to manually resize or reformat - just download and upload.</div>
            </div>
            
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ color: '#fff', fontWeight: '600', marginBottom: '8px', fontSize: '16px' }}>💰 Affordable for Every Creator</div>
              <div style={{ color: '#94a3b8', fontSize: '14.5px',
            fontWeight: '600', lineHeight: '1.6' }}>Start free with 5 videos per month. Scale to Creator Pro when you are ready. Compare that to hiring a video editor at 500+ per month or spending hours doing it yourself. VlogClip AI pays for itself with your first video.</div>
            </div>
          </div>
          <button onClick={() => { setShowGameChanger(false); setTimeout(() => { const el = document.getElementById('doc-quick-links'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); }} style={{ marginTop: '20px', width: '100%', padding: '12px', background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '10px', color: '#fbbf24', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>✕ Close</button>
        </div>
      )}
    </div>
  );
};

export default DocumentationPanel;